import React from 'react';

const PLACEHOLDER_REGEX = /\{([A-Z_]+)\}/g;

export const resolveTemplateText = (value, placeholders) => {
  if (!value) return '';
  return String(value).replace(PLACEHOLDER_REGEX, (match, key) => {
    const resolved = placeholders?.[key];
    if (resolved === null || resolved === undefined) return '';
    return String(resolved);
  });
};

const sanitizeLine = (value) => {
  if (!value) return '';
  let text = String(value).replace(/\s+/g, ' ').trim();
  text = text.replace(/(\s*\|\s*){2,}/g, ' | ');
  text = text.replace(/^\|\s*|\s*\|$/g, '');
  return text.trim();
};

const resolveLine = (value, placeholders) => sanitizeLine(resolveTemplateText(value, placeholders));

export const QR_POSTER_TEMPLATES = [
  { key: 'classic', label: 'Signature Poster', requiresHero: false, backgroundColor: '#ffffff' },
  { key: 'noir-luxe', label: 'Noir Luxe', requiresHero: true, backgroundColor: '#0f0f0f' },
];

export const QR_POSTER_FIELDS = {
  classic: [
    { key: 'headline', label: 'Headline' },
    { key: 'subheadline', label: 'Subheadline' },
    { key: 'note', label: 'Support Line' },
    { key: 'contactLine', label: 'Contact Line', help: 'Use {PHONE} {EMAIL}' },
    { key: 'addressLine', label: 'Address Line', help: 'Use {ADDRESS}' },
    { key: 'reraLine', label: 'RERA Line', help: 'Use {RERA}' },
  ],
  'noir-luxe': [
    { key: 'brandTag', label: 'Brand Tag' },
    { key: 'headlineLine1', label: 'Headline Line 1' },
    { key: 'headlineEmphasis', label: 'Headline Emphasis' },
    { key: 'headlineLine2', label: 'Headline Line 2' },
    { key: 'subheadline', label: 'Subheadline' },
    { key: 'qrLabel', label: 'QR Label' },
    { key: 'qrActionLine1', label: 'QR Action Line 1' },
    { key: 'qrActionLine2', label: 'QR Action Line 2' },
    { key: 'qrUrl', label: 'QR URL', help: 'Use {WEBSITE}' },
    { key: 'footerBrandName', label: 'Footer Brand Name', help: 'Use {BRAND}' },
    { key: 'footerTagline', label: 'Footer Tagline' },
    { key: 'footerLine1', label: 'Footer Contact Line 1', help: 'Use {PHONE} {EMAIL}' },
    { key: 'footerLine2', label: 'Footer Contact Line 2', help: 'Use {PHONE} {EMAIL}' },
  ],
};

export const QRPosterTemplateClassic = ({
  fields,
  placeholders,
  logoUrl,
  accentColor,
  headlineFont,
  bodyFont,
  qrImageUrl,
  showAddress,
  showRera,
  disclaimerText,
}) => {
  const headline = resolveTemplateText(fields.headline, placeholders) || 'Scan to Connect';
  const subheadline = resolveTemplateText(fields.subheadline, placeholders) || 'WhatsApp us or view listings';
  const note = resolveTemplateText(fields.note, placeholders) || 'Open your camera and scan';
  const contactLine = resolveLine(fields.contactLine, placeholders);
  const contactParts = contactLine
    ? contactLine.split('|').map((item) => item.trim()).filter((item) => item)
    : [];
  const addressLine = resolveTemplateText(fields.addressLine, placeholders);
  const reraLine = resolveTemplateText(fields.reraLine, placeholders);

  return (
    <div className="qrposter-surface qrposter-classic" style={{ fontFamily: bodyFont }}>
      <div className="qrposter-top">
        <div className="qrposter-logo">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" crossOrigin="anonymous" />
          ) : (
            <div className="qrposter-logo-text">
              <span>Douglas &amp;</span>
              <span>McCartney</span>
            </div>
          )}
        </div>
        <div className="qrposter-divider" style={{ backgroundColor: accentColor, opacity: 0.28 }} />
      </div>

      <div className="qrposter-headline" style={{ fontFamily: headlineFont }}>
        {headline}
      </div>
      <div className="qrposter-subheadline">{subheadline}</div>

      <div className="qrposter-qr-card">
        {qrImageUrl ? (
          <img src={qrImageUrl} alt="QR Code" crossOrigin="anonymous" className="qrposter-qr-image" />
        ) : (
          <div className="qrposter-placeholder">Upload QR to preview</div>
        )}
      </div>

      <div className="qrposter-note">{note}</div>

      <div className="qrposter-contact">
        {contactParts.length > 1 ? (
          contactParts.map((item, index) => (
            <React.Fragment key={`${item}-${index}`}>
              <span>{item}</span>
              {index < contactParts.length - 1 && (
                <span className="qrposter-dot" style={{ backgroundColor: accentColor }} />
              )}
            </React.Fragment>
          ))
        ) : contactLine ? (
          <span>{contactLine}</span>
        ) : (
          <>
            <span>Phone required</span>
            <span className="qrposter-dot" style={{ backgroundColor: accentColor }} />
            <span>Email pending</span>
          </>
        )}
      </div>

      <div className="qrposter-footer">
        {showAddress && addressLine && <div className="qrposter-address">{addressLine}</div>}
        {showRera && reraLine && <div className="qrposter-rera">{reraLine}</div>}
        {disclaimerText && (
          <div className="qrposter-disclaimer" title={disclaimerText}>
            {disclaimerText}
          </div>
        )}
      </div>
    </div>
  );
};

export const QRPosterTemplateNoir = ({
  fields,
  placeholders,
  logoUrl,
  accentColor,
  qrImageUrl,
  heroImageUrl,
  variant,
}) => {
  const brandTag = resolveTemplateText(fields.brandTag, placeholders);
  const headlineLine1 = resolveTemplateText(fields.headlineLine1, placeholders);
  const headlineEmphasis = resolveTemplateText(fields.headlineEmphasis, placeholders);
  const headlineLine2 = resolveTemplateText(fields.headlineLine2, placeholders);
  const subheadline = resolveTemplateText(fields.subheadline, placeholders);
  const qrLabel = resolveTemplateText(fields.qrLabel, placeholders);
  const qrActionLine1 = resolveTemplateText(fields.qrActionLine1, placeholders);
  const qrActionLine2 = resolveTemplateText(fields.qrActionLine2, placeholders);
  const qrUrl = resolveTemplateText(fields.qrUrl, placeholders);
  const footerBrandName = resolveTemplateText(fields.footerBrandName, placeholders);
  const footerTagline = resolveTemplateText(fields.footerTagline, placeholders);
  const footerLine1 = resolveLine(fields.footerLine1, placeholders);
  const footerLine2 = resolveLine(fields.footerLine2, placeholders);

  const isLight = variant === 'light';

  return (
    <div
      className={`qrposter-noir${isLight ? ' qrposter-noir--light' : ''}`}
      style={{ '--qrposter-accent': accentColor }}
    >
      <div className="qrposter-noir-texture" />
      <div className="qrposter-noir-ambient" />
      <div className="qrposter-noir-circle" />
      <div className="qrposter-noir-circle qrposter-noir-circle-small" />

      <div className="qrposter-noir-corner qrposter-noir-corner-tl" />
      <div className="qrposter-noir-corner qrposter-noir-corner-tr" />
      <div className="qrposter-noir-corner qrposter-noir-corner-bl" />
      <div className="qrposter-noir-corner qrposter-noir-corner-br" />

      <div className="qrposter-noir-hero">
        {heroImageUrl ? (
          <img src={heroImageUrl} alt="Hero" className="qrposter-noir-hero-image" crossOrigin="anonymous" />
        ) : (
          <div className="qrposter-noir-hero-placeholder">Upload hero image</div>
        )}
        <div className="qrposter-noir-hero-overlay" />
        <div className="qrposter-noir-hero-vignette" />
      </div>

      <div className="qrposter-noir-content">
        {brandTag && <div className="qrposter-noir-tag">{brandTag}</div>}

        <div className="qrposter-noir-headline">
          {headlineLine1 && <span>{headlineLine1}</span>}
          {headlineEmphasis && <em>{headlineEmphasis}</em>}
          {headlineLine2 && <span>{headlineLine2}</span>}
        </div>

        {subheadline && <div className="qrposter-noir-subheadline">{subheadline}</div>}

        <div className="qrposter-noir-divider">
          <span className="qrposter-noir-divider-line" />
          <span className="qrposter-noir-divider-diamond" />
          <span className="qrposter-noir-divider-line" />
        </div>

        <div className="qrposter-noir-qr-section">
          <div className="qrposter-noir-qr-info">
            {(qrActionLine1 || qrActionLine2) && (
              <div className="qrposter-noir-qr-action">
                {qrActionLine1 && <span>{qrActionLine1}</span>}
                {qrActionLine2 && <span>{qrActionLine2}</span>}
              </div>
            )}
            {qrUrl && <div className="qrposter-noir-qr-url">{qrUrl}</div>}
          </div>

          <div className="qrposter-noir-qr-block">
            <div className="qrposter-noir-qr-wrapper">
              {qrImageUrl ? (
                <img src={qrImageUrl} alt="QR Code" className="qrposter-noir-qr-image" crossOrigin="anonymous" />
              ) : (
                <div className="qrposter-noir-qr-placeholder">Upload QR</div>
              )}
            </div>
            {qrLabel && <div className="qrposter-noir-qr-caption">{qrLabel}</div>}
          </div>
        </div>
      </div>

      <div className="qrposter-noir-footer">
        <div className="qrposter-noir-footer-brand">
          {logoUrl && <img src={logoUrl} alt="Logo" crossOrigin="anonymous" />}
          <span>{footerBrandName || 'Douglas & McCartney'}</span>
          {footerTagline && <span className="qrposter-noir-footer-tagline">{footerTagline}</span>}
        </div>
        <div className="qrposter-noir-footer-contact">
          {footerLine1 && <p>{footerLine1}</p>}
          {footerLine2 && <p>{footerLine2}</p>}
        </div>
      </div>
    </div>
  );
};
